import { useEffect, useEffectEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorLike } from '@apollo/client';
import { skipToken, useQuery } from '@apollo/client/react';
import { useLocation, useNavigate } from 'react-router';

import { ILocationFragment, LocationDocument } from 'graphql/types';
import { Loader } from 'common/components/Loader';
import { NotFound } from 'common/components/NotFound';
import { PageDrawer } from 'common/components/PageDrawer';
import { PageTitle } from 'common/components/PageTitle';
import { useApolloError } from 'common/hooks/useApolloError';
import { getUrl } from 'route/utils/getUrl';
import { LocationFormController } from 'organisation/forms';

interface IProps {
    open?: boolean;
    locationId?: string;
    onClose?(): void;
    onCreate?(location: ILocationFragment): void;
}

export const LocationManageDrawer = ({
    open,
    locationId,
    onClose,
    onCreate,
}: IProps) => {
    const [translate] = useTranslation();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { showApolloError } = useApolloError();

    const isEdit = !!locationId;

    const { data, loading, error } = useQuery(
        LocationDocument,
        isEdit
            ? {
                  variables: { id: locationId },
                  fetchPolicy: 'cache-and-network',
              }
            : skipToken
    );
    const location = data?.location;

    const onError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    useEffect(() => {
        if (error) {
            onError(error);
        }
    }, [error]);

    const handleClose = () => {
        if (!!onClose) return onClose();

        const searchParams = new URLSearchParams(routerLocation.search);
        const url = `${getUrl('MANAGEMENT_SETTINGS_LOCATIONS')}?${searchParams}`;

        navigate(url);
    };

    const locationNotFound = isEdit && !loading && !location;

    const title =
        isEdit && location?.title
            ? `${location.title} - ${translate('locationsManagementPage.editLocation')}`
            : translate(
                  isEdit
                      ? 'locationsManagementPage.editLocation'
                      : 'locationsManagementPage.newLocation'
              );

    return (
        <PageDrawer
            closeOnBackdropClick={false}
            open={open}
            onClose={handleClose}
        >
            <PageTitle>{title}</PageTitle>

            {loading && <Loader />}

            {locationNotFound && <NotFound />}

            {!loading && !locationNotFound && (
                <LocationFormController
                    location={location}
                    onClose={handleClose}
                    onCreate={onCreate}
                />
            )}
        </PageDrawer>
    );
};
